import { useState, useMemo } from "react";
import { Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Input } from "@/components/ui";

type Attendee = {
  id: string;
  name: string;
  email: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onAdd: (attendee: Attendee) => void;
  attendees: Attendee[];
  excludeIds?: string[];
};

const AddAttendeeModal = ({
  open,
  onClose,
  onAdd,
  attendees,
  excludeIds = [],
}: Props) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedAttendee, setSelectedAttendee] = useState<Attendee | null>(
    null
  );

  // Filter attendees based on search and exclude already added ones
  const filteredAttendees = useMemo(() => {
    const available = attendees.filter((a) => !excludeIds.includes(a.id));
    if (!searchValue.trim()) return available;

    const search = searchValue.toLowerCase();
    return available.filter(
      (a) =>
        a.name.toLowerCase().includes(search) ||
        a.email.toLowerCase().includes(search)
    );
  }, [attendees, excludeIds, searchValue]);

  const handleAdd = () => {
    if (selectedAttendee) {
      onAdd(selectedAttendee);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchValue("");
    setSelectedAttendee(null);
    onClose();
  };

  return (
    <Modal
      title="Add Attendee"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      <div className="pt-4">
        {/* Description */}
        <p className="text-sm text-black/45 mb-4">
          Add winner manually by selecting from attendee list.
        </p>

        {/* Name Label */}
        <label className="block text-sm font-medium text-black/85 mb-2">
          Name
        </label>

        {/* Search Input */}
        <div className="relative mb-2">
          <Input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search attendee name"
            suffix={<SearchOutlined className="text-black/45" />}
            className="w-full"
          />
        </div>

        {/* Attendee List */}
        <div className="border border-[#D9D9D9] rounded-sm max-h-[240px] overflow-y-auto">
          {filteredAttendees.length === 0 ? (
            <div className="p-4 text-center text-black/45 text-sm">
              No attendees found
            </div>
          ) : (
            filteredAttendees.map((attendee) => (
              <div
                key={attendee.id}
                onClick={() => setSelectedAttendee(attendee)}
                className={`px-4 py-3 cursor-pointer border-b border-[#EBEBEB] last:border-b-0 flex items-center gap-2 transition-colors ${
                  selectedAttendee?.id === attendee.id
                    ? "bg-[#E6F4FF]"
                    : "hover:bg-[#FAFAFA]"
                }`}
              >
                <span
                  className={`text-sm ${
                    selectedAttendee?.id === attendee.id
                      ? "font-medium text-black/85"
                      : "text-black/85"
                  }`}
                >
                  {attendee.name}
                </span>
                <span className="text-sm text-black/45">{attendee.email}</span>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6">
          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={!selectedAttendee}
          >
            Add
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddAttendeeModal;
